import React, { useState, useRef, useEffect, FC, useMemo } from 'react';
import {
  Dropdown,
  Button,
  InputAdornment,
  Input,
  Icon,
  Select,
} from 'tea-component';
import { constant, get } from 'lodash-es';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import './index.scss';

type UnitType = 'K' | 'M' | 'B';

interface RangeValue {
  value: number | string | undefined;
  unit: UnitType;
}
interface RevenueRange {
  min: RangeValue;
  max: RangeValue;
}
interface RevenueRangeProps {
  value: RevenueRange;
  title: string;
  width?: number;
  clickSetDefault?: boolean;
  leftPlaceholder?: string;
  rightPlaceholder?: string;
  onChange: (value: RevenueRange) => void;
}

const UNIT_OPTIONS = [
  { value: 'K', text: 'K' },
  { value: 'M', text: 'M' },
  { value: 'B', text: 'B' },
];

const UNIT_MAP = {
  K: 10 ** 3,
  M: 10 ** 6,
  B: 10 ** 9,
};

const RevenueRangeUnit: FC<RevenueRangeProps> = (props) => {
  const {
    value,
    onChange,
    clickSetDefault = false,
    title,
    leftPlaceholder,
    rightPlaceholder,
    width,
  } = props;
  const dropDown = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const inputMinRef = useRef<HTMLInputElement>(null);
  const inputMaxRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [minValue, setMinValue] = useState('');
  const [minUnit, setMinUnit] = useState<UnitType>('M');
  const [maxValue, setMaxValue] = useState('');
  const [maxUnit, setMaxUnit] = useState<UnitType>('M');
  const [isMinError, setIsMinError] = useState(false);
  const [isMaxError, setIsMaxError] = useState(false);
  const [isMinUnitError, setIsMinUnitError] = useState(false);
  const [isMaxUnitError, setIsMaxUnitError] = useState(false);

  const [errorMsg, setErrorMsg] = useState('');

  const [revenueRangeTitle, setRevenueRangeTitle] = useState('');

  const BMK = 1000;

  const handleConfirm = () => {
    onChange({
      min: {
        value: minValue,
        unit: minUnit,
      },
      max: {
        value: maxValue,
        unit: maxUnit,
      },
    });
    closeDropdownPopover();
  };

  const handleCancel = () => {
    setIsMaxError(false);
    setIsMinError(false);
    setIsMaxUnitError(false);
    setIsMinUnitError(false);
    closeDropdownPopover();
  };

  const closeDropdownPopover = () => {
    const evn = document.createEvent('MouseEvents');
    evn.initEvent('mousedown', true, true);
    document.body.dispatchEvent(evn);
  };

  const handleInputChange = (
    val: string,
    type: 'min' | 'max',
    unitLeft?: UnitType,
    unitRight?: UnitType,
  ) => {
    if (type === 'min') {
      setMinValue(val);

      // 清除input框时清空错误信息
      if (!val) {
        setIsMinError(false);
        setIsMinUnitError(false);
        return;
      }
      // 校验输入内容是否符合规范
      if (!/^[0-9]*[\.]?[0-9]*$/.test(val)) {
        setIsMinError(true);
        setIsMinUnitError(false);
        setErrorMsg('dbi_revenue_error_format_tip');
        return;
      }

      if (Number(val) > BMK) {
        setMinValue(`${BMK}`);
      }

      const targetValue = Number(val) > BMK ? BMK : val;
      // 最小值与最大值进行比较
      if (
        maxValue &&
        Number(targetValue) * UNIT_MAP[unitLeft || minUnit] >
          Number(maxValue) * UNIT_MAP[unitRight || maxUnit]
      ) {
        setIsMinError(true);
        setIsMinUnitError(true);
        setErrorMsg('dbi_min_greater_max');
        return;
      }

      setIsMinError(false);
      setIsMinUnitError(false);
      if (isMaxError) {
        handleInputChange(
          maxValue,
          'max',
          unitLeft || minUnit,
          unitRight || maxUnit,
        );
      }
    } else {
      setMaxValue(val);
      if (!val) {
        setIsMaxError(false);
        setIsMaxUnitError(false);
        return;
      }
      if (!/^[0-9]*[\.]?[0-9]*$/.test(val)) {
        setIsMaxError(true);
        setIsMaxUnitError(false);
        setErrorMsg('dbi_revenue_error_format_tip');
        return;
      }

      if (Number(val) > BMK) {
        setMaxValue(`${BMK}`);
      }

      if (
        minValue &&
        Number(val) * UNIT_MAP[unitRight || maxUnit] <
          Number(minValue) * UNIT_MAP[unitLeft || minUnit]
      ) {
        setIsMaxError(true);
        setIsMaxUnitError(true);
        setErrorMsg('dbi_max_less_min');
        return;
      }

      setIsMaxError(false);
      setIsMaxUnitError(false);

      if (isMinError) {
        handleInputChange(
          minValue,
          'min',
          unitLeft || minUnit,
          unitRight || maxUnit,
        );
      }
    }
  };

  useEffect(() => {
    const minRangeValue = get(value, 'min.value', '');
    const maxRangeValue = get(value, 'max.value', '');
    const minRangeUnit = get(value, 'min.unit', '');
    const maxRangeUnit = get(value, 'max.unit', '');
    const targetMin = minRangeValue
      ? `${minRangeValue}`
      : minRangeValue === 0
      ? '0'
      : '';
    const targetMax = maxRangeValue
      ? `${maxRangeValue}`
      : maxRangeValue === 0
      ? '0'
      : '';
    if(!targetMin && !targetMax){
      setMinValue('100');
      setMaxValue('500');
      setMinUnit('M');
      setMaxUnit('M');
    }else{
      setMinValue(targetMin || 'M');
      setMaxValue(targetMax || 'M');
    }
   
    // setMinUnit(minRangeUnit || 'M');
    // setMaxUnit(maxRangeUnit || 'M');

    let revenueRangeTitle: any = (
      <span className="dbi_revenue-range-unit-select-input-range">
        {formatMessage({ id: 'dbi_input_range' })}
      </span>
    );

    if (targetMin && targetMax) {
      revenueRangeTitle = `${targetMin}${targetMin !== '0'? minRangeUnit: ''} ~ ${targetMax}${targetMax !== '0' ?maxRangeUnit: ''}`;
    }

    if (targetMin && !targetMax) {
      revenueRangeTitle = `${targetMin}${targetMin !== '0'? minRangeUnit: ''} ~ `;
    }

    if (!targetMin && targetMax) {
      revenueRangeTitle = `~ ${targetMax}${targetMax !== '0' ?maxRangeUnit: ''}`;
    }

    setRevenueRangeTitle(revenueRangeTitle);

  }, [JSON.stringify(value), locale, isOpen]);

  // 清除input框
  const handleDeleteRevenueRange = (type: 'min' | 'max') => {
    if (type === 'max') {
      setMaxValue('');
      setIsMaxError(false);
      setIsMaxUnitError(false);
      if (inputMaxRef?.current) {
        inputMaxRef?.current?.focus();
      }
    } else {
      setMinValue('');
      setIsMinError(false);
      setIsMinUnitError(false);
      if (inputMinRef?.current) {
        inputMinRef?.current?.focus();
      }
    }
  };

  const handleInputClick = (type: 'min' | 'max') => {
    if (!clickSetDefault) return;
  };

  const handleOpenChange = (value: boolean) => {
    setIsOpen(value);
    setIsMaxError(false);
    setIsMinError(false);
    setIsMaxUnitError(false);
    setIsMinUnitError(false);
  };

  const handleUnitTypeChange = (value: UnitType, type: 'min' | 'max') => {
    if (type === 'min') {
      setMinUnit(value);
      // 切换单位时触发校验
      handleInputChange(minValue, type, value, maxUnit);
    }

    if (type === 'max') {
      setMaxUnit(value);
      // 切换单位时触发校验
      handleInputChange(maxValue, type, minUnit, value);
    }
  };

  return (
    <Dropdown
      ref={dropDown}
      className="select-item dbi-revenue-range-unit-select-box"
      overlayClassName="dbi-revenue-range-unit-select-popover"
      button={
        <div className="dbi-revenue-range-unit-flex">
          <span className="dbi-revenue-range-unit-select-title">
            {formatMessage({ id: title || 'dbi_revenue_range' })}
          </span>
          <span className='dbi-revenue-range-unit-select-content'>{revenueRangeTitle}</span>
        </div>
      }
      onOpenChange={handleOpenChange}
      style={{ width: width || 300 }}
      appearance="button"
      clickClose={false}
    >
      <div style={{ padding: 16 }} ref={popover}>
        <div className="dbi-revenue-range-unit-select-input">
          <InputAdornment
            className="dbi-revenue-range-unit-box"
            after={
              <Select
                options={UNIT_OPTIONS}
                value={minUnit}
                onChange={(value: UnitType) =>
                  handleUnitTypeChange(value, 'min')
                }
                className={classnames([
                  { 'is-unit-error': isMinUnitError },
                  'unit-select',
                ])}
                overlayClassName="dbi—revenue—range-unit-select-popover"
                matchButtonWidth={true}
              />
            }
            style={{ display: 'flex' }}
          >
            <Input
              ref={inputMinRef}
              value={minValue}
              placeholder={formatMessage({
                id: leftPlaceholder || 'dbi_minimum',
              })}
              onClick={() => handleInputClick('min')}
              onChange={(val) => handleInputChange(val, 'min')}
              style={{ paddingRight: minValue ? 30 : 0 }}
              className={classnames([
                { 'is-error': isMinError },
                'dbi-revenue-range-unit-input',
              ])}
            />
            {minValue ? (
              <Icon
                className="dbi-revenue-range-unit-icon"
                type="dismiss"
                onClick={() => handleDeleteRevenueRange('min')}
              />
            ) : null}
          </InputAdornment>
          <span className="dbi-revenue-range-unit-select-divide">-</span>
          <InputAdornment
            className="dbi-revenue-range-unit-box"
            after={
              <Select
                options={UNIT_OPTIONS}
                value={maxUnit}
                onChange={(value: UnitType) =>
                  handleUnitTypeChange(value, 'max')
                }
                className={classnames([
                  { 'is-unit-error': isMaxUnitError },
                  'unit-select',
                ])}
                overlayClassName="dbi—revenue—range-unit-select-popover"
                matchButtonWidth={true}
              />
            }
            style={{ display: 'flex' }}
          >
            <Input
              ref={inputMaxRef}
              value={maxValue}
              onChange={(val) => handleInputChange(val, 'max')}
              onClick={() => handleInputClick('max')}
              placeholder={formatMessage({
                id: rightPlaceholder || 'dbi_maximum',
              })}
              style={{ paddingRight: maxValue ? 30 : 0 }}
              className={classnames([
                { 'is-error': isMaxError },
                'dbi-revenue-range-unit-input',
              ])}
            />
            {maxValue ? (
              <Icon
                className="dbi-revenue-range-unit-icon"
                type="dismiss"
                onClick={() => handleDeleteRevenueRange('max')}
              />
            ) : null}
          </InputAdornment>
        </div>
        {isMinError || isMaxError ? (
          <div className="dbi-revenue-range-unit-select-input-error">
            {formatMessage({ id: errorMsg })}
          </div>
        ) : null}

        <div className="dbi-revenue-btn">
          <Button
            type="primary"
            className="dbi-revenue-btn-left"
            onClick={handleConfirm}
            disabled={isMinError || isMaxError}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button className="dbi-revenue-btn-right" onClick={handleCancel}>
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </div>
      </div>
    </Dropdown>
  );
};

RevenueRangeUnit.displayName = 'RevenueRangeUnit';

export default RevenueRangeUnit;
